import { useRef, useState } from "react";

import { App } from "../../App";
import { MotionProvider } from "../../design/MotionProvider";
import type { BrowserBootstrap } from "../sessions/api";
import { AuthenticationGate } from "./AuthenticationGate";

type BrowserRootProps = {
  initialBootstrap: BrowserBootstrap;
  retryBootstrap: () => Promise<BrowserBootstrap>;
};

export const BrowserRoot = ({
  initialBootstrap,
  retryBootstrap,
}: BrowserRootProps) => {
  const [bootstrap, setBootstrap] = useState(initialBootstrap);
  const [retrying, setRetrying] = useState(false);
  const retryInFlight = useRef(false);

  if (bootstrap.outcome === "READY") {
    return (
      <App
        gateway={bootstrap.gateway}
        runId={bootstrap.runId}
      />
    );
  }

  const retry = async () => {
    if (retryInFlight.current) {
      return;
    }
    retryInFlight.current = true;
    setRetrying(true);
    try {
      setBootstrap(await retryBootstrap());
    } catch {
      setBootstrap({
        outcome: "UNAVAILABLE",
        gateway: null,
        runId: bootstrap.runId,
        correlationId: null,
      });
    } finally {
      retryInFlight.current = false;
      setRetrying(false);
    }
  };

  return (
    <MotionProvider>
      <AuthenticationGate
        state={bootstrap.outcome}
        correlationId={bootstrap.correlationId}
        retrying={retrying}
        onRetry={() => void retry()}
      />
    </MotionProvider>
  );
};
